import * as React from 'react';
import { Formik, Field } from 'formik';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from 'react-query';

import { buildNavItems, useApi, useAuth, useCerebroNav } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { FormikUtils, HtmlUtils } from '../../shared/utils';
import { EditLoanApplicationTaskDto, LoanApplicationTask, UpdateLoanApplicationTaskType } from '../../shared/models';

import {
	CerebroDrawer,
	CerebroForm,
	CerebroPrimaryButton,
	CerebroHeading,
	CerebroText,
	CerebroLabel,
	CerebroInput,
	CerebroTextarea,
	CerebroYesNo,
} from '../../shared/components';
import { getApiResponseData } from '../../shared/utils/axios';
import { Alert, useTheme } from '@mui/material';

export const EditDocumentDrawer: React.FC<EditDocumentDrawerProps> = ({
	open = false,
	onClose,
	document: requiredDocument,
}) => {
	const api = useApi();
	const theme = useTheme();
	const nav = useCerebroNav();
	const client = useQueryClient();
	const { currentUser } = useAuth();
	const { guid } = useParams<'guid'>();

	const snackbar = useSnackbar();
	const queryClient = useQueryClient();

	const ref = React.useRef<HTMLDivElement>(null);

	const uploadMutation = useMutation(
		async ({ values }: EditDocumentMutationValues) => {
			const documentPatch: EditLoanApplicationTaskDto = {
				updateType: values.updateType,
				applicationTaskTitle: values.applicationTaskTitle,
				summary: values.summary,
				removeUploadedFile: values.removeUploadedFile,
			};

			const { data, error } = await getApiResponseData<LoanApplicationTask>(
				api.patch(CerebroApiEndpoints.RequiredDocuments.Edit(guid, requiredDocument.guid), documentPatch)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return;
		},
		{
			onSuccess: () => {
				queryClient.invalidateQueries(QueryKeys.RequiredDocuments.Root(guid)).finally(() => {
					const navState = buildNavItems(
						currentUser,
						client.getQueryData(QueryKeys.Allocation(guid)),
						client.getQueryData(QueryKeys.Deal.Root(guid)),
						location.pathname,
						client.getQueryData(QueryKeys.Questions.LoanApp),
						client.getQueryData(QueryKeys.Product(guid)),
						client.getQueryData(QueryKeys.RequiredDocuments.Root(guid)),
						client.getQueryData(QueryKeys.Integrations.Config())
					);
					nav.setState(navState);
				});
				snackbar.enqueueSnackbar('Required Document saved.', { variant: 'success' });

				onClose();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, {
					variant: 'error',
				});
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer ref={ref} open={open} onClose={onClose}>
			<CerebroHeading size="h2" component="h3" gutterBottom>
				Edit Document
			</CerebroHeading>
			<CerebroText variant="small" gutterBottom>
				Edit the details of your required document.
			</CerebroText>
			<Formik
				enableReinitialize
				onSubmit={FormikUtils.MutateOnSubmit(uploadMutation.mutate)}
				initialValues={{
					updateType: 'Edit' as UpdateLoanApplicationTaskType,
					applicationTaskTitle: requiredDocument.applicationTaskTitle,
					summary: requiredDocument.summary,
					removeUploadedFile: false,
				}}
			>
				{({ isSubmitting, handleSubmit }) => (
					<CerebroForm
						flatIndicator
						encType="multipart/form-data"
						onSubmit={handleSubmit}
						sx={{ marginTop: 3, paddingRight: 3 }}
						scrollbarOffset={HtmlUtils.CalculateScrollbarOffset(ref.current)}
					>
						<CerebroLabel htmlFor="applicationTaskTitle">Title</CerebroLabel>
						<Field
							id="applicationTaskTitle"
							name="applicationTaskTitle"
							as={CerebroInput}
							placeholder="Title..."
							disabled={isSubmitting}
						/>

						<CerebroLabel htmlFor="summary">Summary</CerebroLabel>
						<Field
							id="summary"
							name="summary"
							as={CerebroTextarea}
							rows={8}
							placeholder="Summary of the document request..."
							disabled={isSubmitting}
						/>

						{requiredDocument.isCustomExampleFile && (
							<>
								<CerebroLabel htmlFor="removeUploadedFile" sx={{ marginTop: 6 }}>
									Do you want to remove the example file?
								</CerebroLabel>
								<Field
									id="removeUploadedFile"
									as={CerebroYesNo}
									name="removeUploadedFile"
									disabled={isSubmitting}
								/>
							</>
						)}

						{requiredDocument.hasTemplate && !requiredDocument.isCustomExampleFile && (
							<Alert
								severity="info"
								sx={{
									marginBottom: 3,
									boxShadow: '2px 2px 2px 2px ' + theme.palette.primary.main,
									border: 'none',
									backgroundColor: 'white',
									fontWeight: 'bold',
								}}
							>
								Example files can be removed using the Loan Application - Required Documents report.
							</Alert>
						)}

						<CerebroPrimaryButton loading={isSubmitting} type="submit" sx={{ marginTop: 3 }}>
							Save Details
						</CerebroPrimaryButton>
					</CerebroForm>
				)}
			</Formik>
		</CerebroDrawer>
	);
};

interface EditDocumentDrawerProps {
	open?: boolean;
	onClose: () => void;

	document: LoanApplicationTask;
}

type EditDocumentMutationValues = FormikUtils.MutateOnSubmitVariables<LoanApplicationTaskValues>;

interface LoanApplicationTaskValues {
	applicationTaskTitle: string;
	summary: string;
	updateType: UpdateLoanApplicationTaskType;
	removeUploadedFile: boolean;
}
